const { generateExcelSheet } = require('../utils/generate-excel-sheet')
const { warehouseCodeToVirtualId, warehouseCodeToVirtualProps } = require('../order/tools')
const { locationIdToProperties } = require('../tools')
const get = require('lodash/get')
const keyBy = require('lodash/keyBy')
const endOfDay = require('date-fns/end_of_day')
const startOfDay = require('date-fns/start_of_day')

const SHIPMENTEXPORTHEADERS = [
  'locationCode',
  'locationName',
  'state',
  'lga',
  'ward',
  'subOrderId',
  'funderId',
  'programId',
  'shipmentId',
  'totalLoadVolume',
  'route'
]

const exportWareHouseFunderShipments = async function (
  state,
  { api, warehouse, funderIds, startDate, endDate, programs, dryRun = false, tenant = 'PSM', online = false, statuses = ['new'], omitSubOrderId = false }
) {
  if (
    !warehouse ||
    warehouse === '' ||
    !funderIds ||
    funderIds.length === 0 ||
    !programs ||
    programs.length === 0
  ) {
    throw new Error('Missing warehouse or funder or program')
  }

  const isStateUser = state.user.roles.includes('feature:userRole:psm-state')
  // Get all locations
  const locations = await api.location.listAll({
    dontFilterServices: true,
    includeInactive: true
  })
  const locationsById = keyBy(locations, '_id')

  const userState = isStateUser
    ? state.user.location.state ||
      get(locationIdToProperties(state.user.location.id), 'state', null)
    : null

  let matchingShipments = []

  if (online) {
    // Use Postgres-based query via listLastMileDeliveries
    // Get the base warehouse ID (without funder suffix) for Postgres origin_id matching
    const warehouseProps = warehouseCodeToVirtualProps(warehouse, tenant)
    const baseWarehouseId = warehouseProps._id

    // Build filters for Postgres query
    const baseFilters = {
      origin_id: `${baseWarehouseId}%`, // ILIKE pattern to match warehouse
      status: statuses, // Support array of statuses
      created_at_gte: startOfDay(new Date(startDate)).toISOString(),
      created_at_lte: endOfDay(new Date(endDate)).toISOString()
    }

    // Get shipments from Postgres using listLastMileDeliveries
    // We need to query for each funder-program combination separately since Postgres
    // stores funder_id without the 'funder:' prefix and we need exact matches
    const pgShipmentsPromises = funderIds.map((funderId) => {
      return programs.map((programId) => {
        const funderProgramFilters = {
          ...baseFilters,
          funder_id: funderId,
          program_id: programId
        }
        return api.shipment.listLastMileDeliveries({
          filters: funderProgramFilters,
          forDownload: true,
          addSnapshotsData: false
        })
      })
    }).flat()

    let pgShipments = []
    try {
      const pgShipmentsResults = await Promise.all(pgShipmentsPromises)
      pgShipments = pgShipmentsResults.flatMap(result => result.shipments || [])
    } catch (err) {
      // In test/offline environments, Postgres may be unavailable or validations differ.
      // The online mode should not throw unexpected errors; fall back to no results.
      pgShipments = []
    }

    // Additional filtering by created_at date range (for more precise date filtering)
    // and state if applicable
    const endDateTime = endOfDay(new Date(endDate))
    const startDateTime = startOfDay(new Date(startDate))
    matchingShipments = pgShipments.filter((shipment) => {
      const createdAt = new Date(shipment.createdAt || shipment.created_at)
      const isInDateRange = createdAt >= startDateTime && createdAt <= endDateTime

      // For state filtering, check if destination location matches user's state
      const destinationId = (shipment.destination && shipment.destination.id) || shipment.destination_id
      const matchesState = userState === null || (
        destinationId &&
        get(locationsById[destinationId], 'location.state') === userState
      )

      // Exclude shipments that are already routed.
      // A shipment is considered routed if it has a route_id.
      const hasRoute = !!shipment.route_id

      return isInDateRange && matchesState && !hasRoute
    })
  } else {
    // Use CouchDB-based query (original implementation)
    // We generate all the {funderId, programId} combinations
    const funderProgramCombinations = funderIds
      .map((funderId) => programs.map((programId) => ({ funderId, programId })))
      .flat()

    const warehousesIds = funderProgramCombinations.reduce(
      (acc, { funderId, programId }) => {
        const warehouseId = warehouseCodeToVirtualId({
          warehouseCode: warehouse,
          funderId,
          programId,
          tenant
        })

        if (acc.includes(warehouseId)) {
          return [...acc]
        }

        return [...acc, warehouseId]
      },
      []
    )

    // Get all shipments for that warehouse
    const shipmentPromises = warehousesIds.map((warehouseId) =>
      // Format dates to account for last second of selected end date
      api.shipment.find({
        location: warehouseId,
        startdate: startOfDay(new Date(startDate)).toISOString(),
        enddate: endOfDay(new Date(endDate)).toISOString()
      })
    )
    const shipments = (await Promise.all(shipmentPromises)).flat()

    // Filter for shipments with specified statuses and by specified funder and program
    // Get all columns from filtered shipments
    matchingShipments = shipments.filter((shipment) => {
      // Exclude shipments that are already routed.
      // A shipment is considered routed if it has a routeId.
      const hasRoute = !!shipment.routeId

      return (
        statuses.includes(shipment.status) &&
        funderIds.includes(shipment.funderId) &&
        (userState === null || get(shipment, 'destination.state') === userState) &&
        (shipment.programId && programs.includes(shipment.programId)) &&
        !hasRoute // Exclude already-routed shipments
      )
    })
  }

  let shipmentVolumes
  if (online && matchingShipments.length > 0) {
    // Use SQL-based volume calculation for the Postgres path.
    // The JS-based calculateVolumeAndPrice produces 0 volume for PSM because
    // fromRelationalModel creates batch IDs with manufacturer:unknown (Postgres
    // data_shipmentline only stores sku, not batch details). The SQL query in
    // calculateVolume correctly resolves manufacturer-specific products by joining
    // data_shipmentline_details which has the actual batch string.
    const shipmentIds = matchingShipments.map(s => s.id)
    const volumeRows = await api.shipment.calculateVolume({ shipmentIds })
    const volumeByShipmentId = {}
    for (const row of volumeRows) {
      volumeByShipmentId[row.shipment_id] = parseFloat(row.total_volume) || 0
    }
    shipmentVolumes = matchingShipments.map(s => ({
      totalVolume: volumeByShipmentId[s.id] || 0
    }))
  } else {
    // CouchDB offline path: use JS-based calculation (batch IDs have real manufacturer info)
    shipmentVolumes = await Promise.all(
      matchingShipments.map((shipment) =>
        api.shipment.calculateVolumeAndPrice(shipment, api)
      )
    )
  }

  const shipmentRows = matchingShipments.map((shipment, index) => {
    // Extract destination ID - Postgres shipments have destination.id, CouchDB has destination.id directly
    const destination = online
      ? ((shipment.destination && shipment.destination.id) || shipment.destination_id)
      : shipment.destination.id

    const programId = shipment.programId
    const location = get(locationsById, destination)
    const orderId = shipment.orderId
    const subOrderId = orderId && orderId.includes('suborderId:')
      ? orderId.split('suborderId:')[1] || ''
      : ''
    const volume = shipmentVolumes[index].totalVolume

    // Extract funder ID - Postgres stores without 'funder:' prefix, CouchDB includes it
    const funderId = online
      ? ((shipment.funder && shipment.funder.id) || shipment.funderId || '') // Postgres funder_id is already without prefix
      : shipment.funderId.split('funder:')[1]

    // Extract route name - Postgres has route.name, CouchDB may have routeId but not route name
    const routeName = online
      ? ((shipment.route && shipment.route.name) || '')
      : ((shipment.route && shipment.route.name) || shipment.routeName || '')

    let row = [
      get(location, 'additionalData.warehouseCode', ''),
      get(location, 'name'),
      get(location, 'location.state'),
      get(location, 'location.lga'),
      get(location, 'additionalData.ward'),
      subOrderId,
      funderId,
      programId,
      shipment.id,
      volume,
      routeName
    ]

    if (omitSubOrderId) {
      // Remove the subOrderId entry aligning with SHIPMENTEXPORTHEADERS index
      const subOrderIdx = SHIPMENTEXPORTHEADERS.indexOf('subOrderId')
      if (subOrderIdx !== -1) {
        row.splice(subOrderIdx, 1)
      }
    }

    return row
  })
  const headers = omitSubOrderId
    ? SHIPMENTEXPORTHEADERS.filter(h => h !== 'subOrderId')
    : SHIPMENTEXPORTHEADERS
  const shipmentLocations = [headers, ...shipmentRows]

  if (dryRun) return shipmentLocations
  // Generate excel sheet
  return generateExcelSheet(shipmentLocations, 'Warehouse shipment export')
}
module.exports = { exportWareHouseFunderShipments }
